
import React from 'react';
import { HashRouter as Router, Link, useLocation } from 'react-router-dom';
import { NAV_ITEMS } from '../constants';
import { Printer, MessageCircle, Trophy, User, Fingerprint } from 'lucide-react';

const XLogo = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} width="16" height="16">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  const getIcon = (name: string) => {
    switch (name) {
      case 'the_life': return <User size={20} />;
      case 'money_printer': return <Printer size={20} />;
      case 'fake_id': return <Fingerprint size={20} />;
      case 'vip_lounge': return <MessageCircle size={20} />;
      case 'tax_evasion': return <Trophy size={20} />;
      default: return <User size={20} />;
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8 flex flex-col items-center justify-start">
      
      <div className="w-full max-w-5xl relative">
        
        {/* Binder Rings - Redesigned to look like rings going through holes */}
        <div className="flex justify-center mb-0 gap-16 md:gap-32 absolute -top-8 left-0 right-0 z-20 pointer-events-none">
           {[1,2,3].map(i => (
             <div key={i} className="flex flex-col items-center relative">
                {/* The Top part of the Ring (Behind the page) */}
                <div className="absolute -top-4 w-12 h-12 rounded-full border-[6px] border-gray-400 border-b-transparent z-0"></div>
                
                {/* The Paper Hole */}
                <div className="w-6 h-6 bg-gray-800 rounded-full border-2 border-gray-300 shadow-inner mt-8 z-10"></div>
                
                {/* The Ring coming through */}
                <div className="absolute top-2 w-4 h-16 border-x-[4px] border-gray-300 bg-gradient-to-r from-gray-100 to-gray-400 rounded-full shadow-lg z-20"></div>
             </div>
           ))}
        </div>

        {/* Navigation Tabs (Top) */}
        <div className="flex pl-4 md:pl-12 gap-1 md:gap-2 overflow-x-auto pb-0 pt-6 no-scrollbar items-end">
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`
                  px-4 md:px-6 py-2 md:py-3 
                  border-t-4 border-x-4 border-black 
                  font-bold text-base md:text-lg tracking-tight
                  rounded-t-xl
                  transition-all
                  whitespace-nowrap
                  flex items-center gap-2
                  shrink-0
                  ${isActive 
                    ? 'bg-white -mb-[4px] pb-4 z-10 text-olek-pink-dark scale-105 origin-bottom' 
                    : 'bg-gray-300 hover:bg-gray-200 text-gray-600 mb-0 mt-2'}
                `}
              >
                {getIcon(item.name)}
                <span className="lowercase">{item.name.replace('_', ' ')}</span>
              </Link>
            );
          })}
        </div>

        {/* Main Page Container */}
        <main className="
          bg-white 
          border-4 border-black 
          shadow-hard-lg 
          min-h-[80vh] 
          relative 
          z-0
          paper-texture
        ">
          {/* Pink Margin Line */}
          <div className="absolute top-0 left-8 md:left-16 bottom-0 w-[2px] bg-olek-pink/50 z-0 pointer-events-none"></div>

          <div className="relative z-10 p-6 md:p-12 pl-12 md:pl-24">
             {children}
          </div>

          {/* Footer Doodle */}
          <div className="absolute bottom-4 right-4 flex flex-col items-end gap-2">
            
            {/* X Link */}
            <a href="https://x.com/OleksandrePump" target="_blank" rel="noopener noreferrer" className="bg-black text-white p-2 rounded-lg border-2 border-gray-400 hover:scale-110 transition-transform">
               <XLogo />
            </a>

            <div className="text-xs font-bold text-gray-400 -rotate-2">
              $OLEK // born rich // never wagie
            </div>
          </div>
        </main>
      </div>
      
    </div>
  );
};

export default Layout;
